// apps/admin-ui/src/components/RoleDashboard.tsx
// Config-driven role dashboard that renders widgets from role layout
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-01: Added pending-withdrawals widget case
// UPDATED 2026-02-01: Added real data fetching for pending-mints and pending-withdrawals widgets
// UPDATED 2026-02-02: Added dex-onramp-activity widget for crypto funding
// UPDATED 2026-02-04: Added contract-rules-summary widget
// UPDATED 2026-02-04: Added pending-reviews widget (PendingContractReviews)
// UPDATED 2026-02-07: Added audit-events widget (AuditEvents) for security dashboard

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Role } from '../utils/rbac';
import type { RoleDashboardLayout } from '../config/dashboardWidgets';
import {
  getWidgetsForRole,
  getDrillDownUrl,
  showsExecutiveOverview,
} from '../config/dashboardWidgets';
import { DashboardLayout } from './DashboardLayout';
import { WidgetStatusBadge } from './WidgetStatusBadge';
import { ExecutiveOverview } from './ExecutiveOverview';
import { StatusCard } from './StatusCard';
import { MetricCard } from './MetricCard';
import { FeeDistribution } from './FeeDistribution';
import { VolumeChart } from './VolumeChart';
import { AnchorStatus } from './AnchorStatus';
// New widgets
import { AttestationStatus } from './widgets/AttestationStatus';
import { ReserveVerification } from './widgets/ReserveVerification';
import { AuditReports } from './widgets/AuditReports';
import { AssetHoldings } from './widgets/AssetHoldings';
import { DexOnRampActivity } from './widgets/DexOnRampActivity';  // NEW
import { ContractRulesWidget } from './widgets/ContractRulesWidget';  // NEW
import { PendingContractReviews } from './widgets/PendingContractReviews';  // NEW
import { KycThroughput } from './widgets/KycThroughput';  // NEW: supervisor KYC dashboard
import { AuditEvents } from './widgets/AuditEvents';  // NEW: security audit events
import { useDashboard } from '../hooks/useDashboard';
import { api } from '../api/client';
import '../styles/role-dashboard.css';

// =============================================================================
// TYPES
// =============================================================================

export interface RoleDashboardProps {
  layout: RoleDashboardLayout;
  role: Role;
  employee: {
    id: string;
    name: string;
    email: string;
    roles: Role[];
  };
}

interface WidgetState {
  isLoading: boolean;
  error: Error | null;
  lastRefresh: Date | null;
}

interface TransferStats {
  pending: number;
  completed: number;
  failed: number;
  total: number;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatNumber(value: string | number, decimals = 0): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

// =============================================================================
// WIDGET RENDERER
// =============================================================================

interface WidgetContentProps {
  widgetId: string;
  dashboard: any;
  systemStatus: any;
  withdrawalStats?: TransferStats;
  mintStats?: TransferStats;
}

function renderWidgetContent({ widgetId, dashboard, systemStatus, withdrawalStats, mintStats }: WidgetContentProps) {
  const { summary, performance, reserve, anchors, charts } = dashboard || {};

  switch (widgetId) {
    case 'system-status':
      return systemStatus ? <StatusCard status={systemStatus} /> : (
        <div className="widget-placeholder">Loading system status...</div>
      );

    case 'reserve-ratio':
      return (
        <MetricCard
          title="Reserve Ratio"
          value={reserve?.ratio ? `${formatNumber(reserve.ratio, 2)}:1` : '2.04:1'}
          subtitle={reserve?.ratio >= 2 ? '✓ Above 2:1 minimum' : '⚠ Below 2:1 minimum'}
          status={reserve?.ratio >= 2 ? 'success' : 'warning'}
        />
      );

    case 'peg-status':
      return (
        <MetricCard
          title="USD Peg"
          value={reserve?.pegPrice ? `$${formatNumber(reserve.pegPrice, 4)}` : '$1.0002'}
          subtitle="● Stable"
          status="success"
        />
      );

    case 'transaction-volume':
      return (
        <MetricCard
          title="Transaction Volume"
          value={formatCurrency(summary?.transactionVolume || 4200000)}
          subtitle="30-day volume"
          trend={{ direction: 'up', value: '+12% MoM' }}
        />
      );

    case 'fees-collected':
      return (
        <MetricCard
          title="Fees Collected"
          value={formatCurrency(summary?.feesCollected || 21847)}
          subtitle="30-day total"
          trend={{ direction: 'up', value: '+8% MoM' }}
        />
      );

    case 'fee-distribution':
      return <FeeDistribution
        total={String(summary?.feesCollected || 21847)}
        cxxr={String((summary?.feesCollected || 21847) * 0.5)}
        ccc={String((summary?.feesCollected || 21847) * 0.3)}
        reserve={String((summary?.feesCollected || 21847) * 0.2)}
      />;

    case 'volume-chart':
      return <VolumeChart data={charts?.volume} />;

    case 'anchor-chain':
      return <AnchorStatus anchors={anchors} />;

    case 'performance-metrics':
      return (
        <MetricCard
          title="API Performance"
          value={performance?.avgLatencyMs ? `${formatNumber(performance.avgLatencyMs, 0)}ms` : '847ms'}
          subtitle={`P95: ${performance?.p95LatencyMs || 1240}ms`}
          status={(performance?.avgLatencyMs || 847) <= 1000 ? 'success' : 'warning'}
        />
      );

    case 'kyc-pending':
      return (
        <MetricCard
          title="KYC Pending"
          value={formatNumber(summary?.kycPending || 12, 0)}
          subtitle="Awaiting review"
          status={(summary?.kycPending || 0) > 10 ? 'warning' : 'neutral'}
        />
      );

    case 'aml-alerts':
      return (
        <MetricCard
          title="AML Alerts"
          value={formatNumber(summary?.amlAlerts || 3, 0)}
          subtitle="Open alerts"
          status={(summary?.amlAlerts || 0) > 5 ? 'error' : (summary?.amlAlerts || 0) > 0 ? 'warning' : 'success'}
        />
      );

    case 'vulnerabilities':
      return (
        <MetricCard
          title="Open Vulnerabilities"
          value={formatNumber(summary?.vulnerabilities || 2, 0)}
          subtitle="2 critical · 5 high"
          status={(summary?.vulnerabilities || 0) > 0 ? 'warning' : 'success'}
        />
      );

    case 'pentest-status':
      return (
        <MetricCard
          title="Days Since Pen Test"
          value="47"
          subtitle="Next: 43 days"
          status="neutral"
        />
      );

    case 'last-deployment':
      return (
        <MetricCard
          title="Last Deployment"
          value="14d ago"
          subtitle="v2.4.1 • FeeContract"
          status="neutral"
        />
      );

    // =========================================================================
    // DEPOSIT ACTIVITY - Deposits auto-complete via ACH webhook
    // =========================================================================
    case 'pending-mints':
      if (!mintStats) {
        return (
          <div className="widget-placeholder">
            <p>Loading deposit activity...</p>
          </div>
        );
      }
      return (
        <div className="transfer-stats-widget">
          <div className="stats-grid">
            <div className="stat-item completed">
              <span className="stat-value">{mintStats.completed}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item pending">
              <span className="stat-value">{mintStats.pending}</span>
              <span className="stat-label">Processing</span>
            </div>
            <div className="stat-item failed">
              <span className="stat-value">{mintStats.failed}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
          <div className="stats-summary">
            <small>{mintStats.total} total deposits</small>
          </div>
        </div>
      );

    // =========================================================================
    // PENDING WITHDRAWALS - Real data from API
    // =========================================================================
    case 'pending-withdrawals':
      if (!withdrawalStats) {
        return (
          <div className="widget-placeholder">
            <p>Loading withdrawal requests...</p>
          </div>
        );
      }
      return (
        <div className="transfer-stats-widget">
          <div className="stats-grid">
            <div className="stat-item pending">
              <span className="stat-value">{withdrawalStats.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item completed">
              <span className="stat-value">{withdrawalStats.completed}</span>
              <span className="stat-label">Approved</span>
            </div>
            <div className="stat-item failed">
              <span className="stat-value">{withdrawalStats.failed}</span>
              <span className="stat-label">Denied</span>
            </div>
          </div>
          <div className="stats-summary">
            <small>{withdrawalStats.total} total withdrawal requests</small>
          </div>
        </div>
      );

    // =========================================================================
    // DEX ONRAMP ACTIVITY - Crypto funding via DEX (NEW)
    // =========================================================================
    case 'dex-onramp-activity':
      return <DexOnRampActivity />;

    case 'kyc-queue-preview':
      return (
        <div className="widget-placeholder">
          <p>KYC Queue Preview</p>
          <small>12 pending · 3 in review</small>
        </div>
      );

    // =========================================================================
    // KYC THROUGHPUT - Supervisor-level processed counts & per-CSR breakdown
    // =========================================================================
    case 'kyc-throughput':
      return <KycThroughput />;

    case 'security-alerts':
      return (
        <div className="widget-placeholder">
          <p>Security Alerts</p>
          <small>No critical alerts</small>
        </div>
      );

    case 'deployments':
      return (
        <div className="widget-placeholder">
          <p>Recent Deployments</p>
          <small>3 deployments this month</small>
        </div>
      );

    case 'governance-proposals':
      return (
        <div className="widget-placeholder">
          <p>Active Proposals</p>
          <small>1 proposal awaiting vote</small>
        </div>
      );

    case 'transactions-preview':
      return (
        <div className="widget-placeholder">
          <p>Recent Transactions</p>
          <small>1,247 transactions today</small>
        </div>
      );

    // CPA Auditor widgets
    case 'attestation-status':
      return <AttestationStatus />;

    case 'reserve-verification':
      return <ReserveVerification />;

    case 'audit-reports':
      return <AuditReports />;

    // Custodian widgets
    case 'asset-holdings':
      return <AssetHoldings />;

    case 'reserve-composition':
      return (
        <div className="widget-placeholder">
          <p>Reserve Composition</p>
          <small>Cash 45% | T-Bills 30% | RE 15% | Other 10%</small>
        </div>
      );

    case 'treasury-balance':
      return (
        <MetricCard
          title="Treasury Balance"
          value={formatCurrency(reserve?.treasuryBalance || 8400000)}
          subtitle="Available funds"
          status="neutral"
        />
      );

    case 'circulation':
      return (
        <MetricCard
          title="CXXR Circulation"
          value={formatNumber(reserve?.circulation || 4120000, 0)}
          subtitle="Total supply"
          status="neutral"
        />
      );

    // Contract rules summary widget
    case 'contract-rules-summary':
      return <ContractRulesWidget />;

    // Contract auditor - pending code reviews (LIVE)
    case 'pending-reviews':
      return <PendingContractReviews />;

    // Security admin - live audit events from KYC service
    case 'audit-events':
      return <AuditEvents />;

    default:
      return (
        <div className="widget-placeholder">
          <p>Unknown widget: {widgetId}</p>
        </div>
      );
  }
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function RoleDashboard({ layout, role, employee }: RoleDashboardProps) {
  const {
    dashboard,
    systemStatus,
    isLoading,
    error,
    lastRefresh,
    refresh,
  } = useDashboard();

  const [widgetStates] = useState<Record<string, WidgetState>>({});
  
  // State for withdrawal and mint statistics
  const [withdrawalStats, setWithdrawalStats] = useState<TransferStats | undefined>();
  const [mintStats, setMintStats] = useState<TransferStats | undefined>();
  const [statsLoading, setStatsLoading] = useState(false);

  // Get widgets for this role
  const widgets = getWidgetsForRole(role);

  // Check if role should see executive overview
  const shouldShowExecutiveOverview = showsExecutiveOverview(role) && layout.showExecutiveOverview;

  // Check if this role needs transfer stats
  const widgetIds = widgets.map(w => w.id);
  const needsWithdrawalStats = widgetIds.includes('pending-withdrawals');
  const needsMintStats = widgetIds.includes('pending-mints');
  const needsStats = needsWithdrawalStats || needsMintStats;

  // Fetch all stats in ONE call
  const fetchStats = useCallback(async () => {
    if (!needsStats || statsLoading) return;
    
    setStatsLoading(true);
    try {
      const stats = await api.get('/api/v1/admin/stats');
      
      if (needsWithdrawalStats) {
        setWithdrawalStats(stats.withdrawals);
      }
      if (needsMintStats) {
        setMintStats(stats.mints);
      }
    } catch (err) {
      console.error('Failed to fetch transfer stats:', err);
      // Set zeros on error
      if (needsWithdrawalStats) {
        setWithdrawalStats({ pending: 0, completed: 0, failed: 0, total: 0 });
      }
      if (needsMintStats) {
        setMintStats({ pending: 0, completed: 0, failed: 0, total: 0 });
      }
    } finally {
      setStatsLoading(false);
    }
  }, [needsStats, needsWithdrawalStats, needsMintStats, statsLoading]);

  // Fetch stats on mount only (not on every lastRefresh to avoid spam)
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle refresh all - also refresh our custom stats
  const handleRefreshAll = useCallback(() => {
    refresh();
    fetchStats();
  }, [refresh, fetchStats]);

  // Loading state
  if (isLoading && !dashboard) {
    return (
      <div className="dashboard-layout loading">
        <div className="spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  // Error state
  if (error && !dashboard) {
    return (
      <div className="dashboard-layout error">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={handleRefreshAll}>Retry</button>
      </div>
    );
  }

  // No widgets available for this role
  if (widgets.length === 0) {
    return (
      <div className="dashboard-layout empty">
        <h2>No Dashboard Access</h2>
        <p>Your role does not have access to any dashboard widgets.</p>
        <p className="role-info">Current role: {role}</p>
      </div>
    );
  }

  // Derive system status for the status bar
  const systemStatusData = systemStatus ? {
    status: (systemStatus.status === 'NORMAL' ? 'operational' : 'degraded') as 'operational' | 'degraded' | 'outage',
    uptime: 99.9,
    queueDepth: systemStatus.queueDepth || 0,
  } : {
    status: 'operational' as const,
    uptime: 99.97,
    queueDepth: 12,
  };

  return (
    <DashboardLayout
      layout={layout}
      role={role}
      employee={employee}
      systemStatus={systemStatusData}
      lastRefresh={lastRefresh}
      isLoading={isLoading}
      onRefreshAll={handleRefreshAll}
      executiveOverview={
        shouldShowExecutiveOverview ? (
          <ExecutiveOverview
            dashboard={dashboard}
            systemStatus={systemStatus}
          />
        ) : undefined
      }
    >
      {widgets.map(widget => {
        const drillDownUrl = getDrillDownUrl(role, widget.id);
        const widgetState = widgetStates[widget.id] || {
          isLoading: false,
          error: null,
          lastRefresh: null,
        };

        return (
          <div
            key={widget.id}
            className={`widget-card size-${widget.size} ${drillDownUrl ? 'has-drilldown' : ''}`}
          >
            <div className="widget-card-header">
              <h3 className="widget-card-title">{widget.title}</h3>
              <WidgetStatusBadge
                refreshInterval={widget.refreshInterval}
                isMock={widget.isMock || false}
                error={widgetState.error}
              />
            </div>

            <div className="widget-card-content">
              {renderWidgetContent({
                widgetId: widget.id,
                dashboard,
                systemStatus,
                withdrawalStats,
                mintStats,
              })}
            </div>

            {drillDownUrl && (
              <Link to={drillDownUrl} className="widget-drilldown-link">
                View details →
              </Link>
            )}
          </div>
        );
      })}
    </DashboardLayout>
  );
}

export default RoleDashboard;




